import Link from "next/link";
import styled, { css } from "styled-components";
import { Flex } from "../../components/Grid";
import { Icon } from "../../components/Icon";
import {
  Caption1,
  Caption2,
  FontSplitupProps,
  Footnote,
  Headline,
  Subhead,
} from "../../components/Typography";
import { TextMarquee } from "../../components/animation/TextMarquee";
import { Creator, DropEntry } from "../../dropTypes";
import {
  hideAboveMobile,
  hideOnMobile,
  xSmallScreen,
} from "../../lib/mediaQuery";
import { formatDollarsFromCents } from "../../utils/format";
import { dropTypePrettifier } from "../../utils/strings";
import { ThumbnailContainer, ThumbnailImage } from "./dropImage";
import { ThumbnailAudioPreview } from "./mediaPreview/thumbnailAudioPreview";
import { ThumbnailUserDetails } from "./userDetails";

interface Props {
  drop: DropEntry;
  onClick?: () => void;
  href?: string;
  isPending?: boolean;
  creator?: Creator;
}

export const DropThumbnail = ({
  drop,
  href,
  onClick,
  isPending = false,
  creator = drop.creator,
}: Props) => {
  const isSoldOut = drop?.variants?.[0]?.itemsRemaining === 0;
  const isEnded = drop?.state?.kind === "Ended";

  return (
    <Link
      href={href}
      css={`
        color: inherit;
        :hover {
          text-decoration: none;
          color: inherit;
        }
      `}
    >
      <ThumbnailContainer
        direction={"column"}
        onClick={onClick}
        data-is-disabled={isPending}
      >
        {isPending && <DisabledOverlay />}
        <ThumbnailImage
          imageUrl={drop?.data?.image}
          drop={drop}
          isPending={isPending}
        />
        <ThumbnailAudioPreview drop={drop} />
        <ThumbnailFooter direction={"column"}>
          <Headline
            css={`
              max-width: 218px;
              ${hideOnMobile}
            `}
          >
            <TextMarquee>{drop?.data?.name}</TextMarquee>
          </Headline>
          <Subhead
            css={`
              max-width: 155px;
              font-weight: 600;
              ${hideAboveMobile}
            `}
          >
            <TextMarquee>{drop?.data?.name}</TextMarquee>
          </Subhead>
          <ThumbnailUserDetails creator={creator} />
          <DetailsContainer>
            <Detail direction={"column"} flex={1}>
              <DetailTitle>Type</DetailTitle>
              <Flex
                align={"center"}
                css={`
                  gap: 2px;
                `}
              >
                <CDIcon name="CD" />
                <DetailDescription>
                  {dropTypePrettifier(
                    drop?.variants?.[0]?.collectionMetadata?.type
                  )}
                </DetailDescription>
              </Flex>
            </Detail>
            <Detail direction={"column"} flex={1}>
              <DetailTitle>Price</DetailTitle>
              <DetailDescription>
                {isSoldOut
                  ? "Sold Out"
                  : isEnded
                  ? "Offer Ended"
                  : formatDollarsFromCents(
                      Number(drop?.variants?.[0].priceUsdCents)
                    )}
              </DetailDescription>
            </Detail>
          </DetailsContainer>
        </ThumbnailFooter>
      </ThumbnailContainer>
    </Link>
  );
};

const DetailTitle = ({ children, className }: FontSplitupProps) => (
  <>
    <Caption1
      css={`
        ${hideOnMobile}
        color: rgb(var(--grey-mid));
        margin-bottom: 2px;
      `}
      className={className}
    >
      {children}
    </Caption1>
    <Caption2
      css={`
        ${hideAboveMobile}
        color: rgb(var(--grey-mid));
        margin-bottom: 2px;
      `}
      className={className}
    >
      {children}
    </Caption2>
  </>
);
const DetailDescription = ({ children, className }: FontSplitupProps) => (
  <>
    <Footnote css={hideOnMobile} className={className}>
      {children}
    </Footnote>
    <Caption1 css={hideAboveMobile} className={className}>
      {children}
    </Caption1>
  </>
);

const borderRadius = css`
  border-radius: 2px;
`;

const DisabledOverlay = styled(Flex)`
  width: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  background: rgba(1, 1, 1, 0.75);
`;

const ThumbnailFooter = styled(Flex)`
  background-color: rgba(255, 255, 255, 0.1);
  z-index: 1;
  margin-top: var(--spacing-xxs);
  border: 1px solid transparent;
  transition: border var(--transition-speed-normal) var(--easing-standard);
  padding: var(--spacing-xs);
  flex-wrap: nowrap;
  ${borderRadius};
  ${xSmallScreen(
    css`
      padding: var(--spacing-xs) var(--spacing-xxs);
    `
  )};
  ${ThumbnailContainer}[data-is-disabled="false"]:hover & {
    backdrop-filter: none;
    border: 1px solid rgb(var(--cyan-mid));
    background-color: rgba(var(--black-dark));
  }
`;

const DetailsContainer = styled(Flex)`
  gap: var(--spacing-xxs);
`;

const CDIcon = styled(Icon)`
  width: 11px;
  height: 11px;
`;

const Detail = styled(Flex)`
  padding: 2px var(--spacing-xxs);
  background: var(--glass-input);
  border-radius: var(--spacing-xxs);
`;
